// Pure-function Jalali/Gregorian calendar engine backing date_picker_controller.
// No DOM state, no side-effects beyond building the DOM nodes it's asked to
// build — safe to import anywhere. Adapted from GreenPay's datetime_engine.js:
// same calendar math, but DOM builders use this app's Tailwind tokens
// (brand/neutral/danger — see app/assets/stylesheets/_tokens.tailwind.css)
// instead of GreenPay's CSS custom properties.
import moment from "jalali-moment"

const FA = [ "۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹" ]
export const toFa = (n) => String(n).replace(/\d/g, (d) => FA[d])

export const MONTHS_FA = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
]

// Index 0 = Saturday (rightmost in RTL)
export const WEEK_HDR = [ "ش", "ی", "د", "س", "چ", "پ", "ج" ]

const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]
const WEEK_HDR_EN = [ "Mo", "Tu", "We", "Th", "Fr", "Sa", "Su" ]

/** Build a jalali moment from y/m/d components */
export function jm(y, m, d) {
  return moment(`${y}/${String(m).padStart(2, "0")}/${String(d).padStart(2, "0")}`, "jYYYY/jMM/jDD")
}

/** Destructure a moment into { y, m, d } (jalali) */
export function fromJm(mo) {
  return { y: mo.jYear(), m: mo.jMonth() + 1, d: mo.jDate() }
}

/** Number of days in a jalali month (handles leap Esfand) */
export function daysInMonth(y, m) {
  if (m <= 6) return 31
  if (m <= 11) return 30
  return jm(y, 12, 1).endOf("jMonth").jDate()
}

/** Day-of-week offset for the 1st of a jalali month: 0 = Saturday … 6 = Friday */
export function firstDow(y, m) {
  const dow = jm(y, m, 1).day() // moment: 0=Sun … 6=Sat
  return (dow + 1) % 7 // shift: Sat→0, Sun→1, … Fri→6
}

/** Is a given jalali date a Friday (Iranian weekend)? */
export function isFriday(y, m, d) {
  return jm(y, m, d).day() === 5
}

/** "۱۴۰۴/۰۳/۱۵" */
export function fmtDate(y, m, d) {
  return `${toFa(y)}/${toFa(String(m).padStart(2, "0"))}/${toFa(String(d).padStart(2, "0"))}`
}

// Every date-math/formatting decision renderMonth makes goes through one of
// these adapters. Controllers pick one via getCal().
export const CAL_JALALI = {
  name: "jalali",
  dir: "rtl",
  months: MONTHS_FA,
  weekHdr: WEEK_HDR,
  digits: toFa,
  prevIcon: "ti-chevron-right",
  nextIcon: "ti-chevron-left",
  today: () => { const n = moment(); return { y: n.jYear(), m: n.jMonth() + 1, d: n.jDate() } },
  now: () => moment().locale("fa"),
  make: jm,
  parts: fromJm,
  daysInMonth,
  firstDow,
  isHoliday: isFriday,
  fmt: fmtDate,
}

export const CAL_GREGORIAN = {
  name: "gregorian",
  dir: "ltr",
  months: MONTHS_EN,
  weekHdr: WEEK_HDR_EN,
  digits: String,
  prevIcon: "ti-chevron-left",
  nextIcon: "ti-chevron-right",
  // .locale('en') is required: with the global 'fa' locale, jalali-moment
  // makes .year()/.month()/.date() return jalali components
  today: () => { const n = moment().locale("en"); return { y: n.year(), m: n.month() + 1, d: n.date() } },
  now: () => moment().locale("en"),
  // moment.from pins the source calendar so the global 'fa' locale can't interfere
  make: (y, m, d) => moment.from(`${y}-${m}-${d}`, "en", "YYYY-M-D").locale("en"),
  parts: (mo) => { const n = mo.clone().locale("en"); return { y: n.year(), m: n.month() + 1, d: n.date() } },
  daysInMonth: (y, m) => CAL_GREGORIAN.make(y, m, 1).daysInMonth(),
  firstDow: (y, m) => (CAL_GREGORIAN.make(y, m, 1).day() + 6) % 7, // Mon→0 … Sun→6
  isHoliday: (y, m, d) => CAL_GREGORIAN.make(y, m, d).day() === 0, // Sunday
  fmt: (y, m, d) => `${y}/${String(m).padStart(2, "0")}/${String(d).padStart(2, "0")}`,
}

export const getCal = (name) => (name === "gregorian" ? CAL_GREGORIAN : CAL_JALALI)

const NAV_BTN_CLASS = "flex size-7 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800"

function navButton(iconClass, onClick) {
  const b = document.createElement("button")
  b.type = "button"
  b.className = NAV_BTN_CLASS
  b.innerHTML = `<i class="ti ${iconClass} text-sm" aria-hidden="true"></i>`
  if (onClick) b.onclick = onClick
  return b
}

/**
 * Renders a complete month calendar into `el`.
 *
 * @param {Element} el          - Container to render into (innerHTML is cleared)
 * @param {object}  opts
 *   year, month                - Year/month to display (in cal's calendar system)
 *   selected      {moment|null}- Highlighted single date
 *   minDate       {moment|null}- Dates before this are disabled
 *   maxDate       {moment|null}- Dates after this are disabled
 *   onDay         {Function}   - Called with moment when a day cell is clicked
 *   onPrev        {Function}   - Called when the previous-month nav is clicked
 *   onNext        {Function}   - Called when the next-month nav is clicked
 *   onHeader      {Function}   - Called when the month/year title is clicked
 *   anim          {string}     - Optional animation class name
 *   cal           {object}     - Calendar adapter (default CAL_JALALI)
 */
export function renderMonth(el, opts) {
  const {
    year, month,
    selected = null,
    minDate = null, maxDate = null,
    onDay, onPrev, onNext, onHeader,
    anim = "",
    cal = CAL_JALALI,
  } = opts

  el.innerHTML = ""
  el.dir = cal.dir

  const header = document.createElement("div")
  header.className = "flex items-center justify-between px-3 pb-2 pt-3"

  const title = document.createElement("button")
  title.type = "button"
  title.className = "rounded-lg px-2 py-1 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-100"
  title.innerHTML = `<span>${cal.months[month - 1]}</span> <span class="text-brand-600">${cal.digits(year)}</span>`
  if (onHeader) title.onclick = onHeader

  header.append(navButton(cal.prevIcon, onPrev), title, navButton(cal.nextIcon, onNext))
  el.appendChild(header)

  const weekRow = document.createElement("div")
  weekRow.className = "flex justify-center px-2 pb-1.5"
  cal.weekHdr.forEach((label, i) => {
    const cell = document.createElement("div")
    cell.className = `flex size-9 items-center justify-center text-xs ${i === 6 ? "text-danger-500" : "text-neutral-400"}`
    cell.textContent = label
    weekRow.appendChild(cell)
  })
  el.appendChild(weekRow)

  const body = document.createElement("div")
  body.className = `px-2 pb-2 ${anim}`

  const today = cal.today()
  const total = cal.daysInMonth(year, month)
  const offset = cal.firstDow(year, month)
  const cells = Math.ceil((offset + total) / 7) * 7
  let dayNum = 1
  let row = null

  for (let i = 0; i < cells; i++) {
    if (i % 7 === 0) {
      row = document.createElement("div")
      row.className = "flex justify-center"
      body.appendChild(row)
    }

    if (i < offset || dayNum > total) {
      const spacer = document.createElement("div")
      spacer.className = "size-9"
      row.appendChild(spacer)
      if (i >= offset) dayNum++
      continue
    }

    const d = dayNum
    const cellMoment = cal.make(year, month, d)
    const isToday = year === today.y && month === today.m && d === today.d
    const isSelected = selected && cellMoment.isSame(selected, "day")
    const isHoliday = cal.isHoliday(year, month, d)
    const disabled = (minDate && cellMoment.isBefore(minDate, "day")) || (maxDate && cellMoment.isAfter(maxDate, "day"))

    const cell = document.createElement("button")
    cell.type = "button"
    cell.className = dayCellClass({ isToday, isSelected, isHoliday, disabled })
    cell.textContent = cal.digits(d)

    if (!disabled) cell.onclick = () => onDay && onDay(cellMoment.clone())
    else cell.disabled = true

    row.appendChild(cell)
    dayNum++
  }

  el.appendChild(body)
}

function dayCellClass({ isToday, isSelected, isHoliday, disabled }) {
  const base = "relative flex size-9 items-center justify-center rounded-full text-sm transition-colors select-none"

  if (isSelected) return `${base} bg-brand-600 font-medium text-white shadow-sm`
  if (disabled) return `${base} cursor-not-allowed text-neutral-300`
  if (isToday) return `${base} cursor-pointer bg-brand-50 font-medium text-brand-700 hover:bg-brand-100`

  const color = isHoliday ? "text-danger-500" : "text-neutral-700"
  return `${base} cursor-pointer ${color} hover:bg-neutral-100`
}

/**
 * Renders a 4×3 month grid with a year stepper into `el` — the overlay
 * shown when the user clicks the month/year title.
 *
 * @param {Element}  el
 * @param {number}   curY     - Currently viewed year
 * @param {number}   curM     - Currently viewed month (1-12)
 * @param {Function} onSelect - Called with (year, month) when a month cell is clicked
 * @param {Function} onClose  - Called when the close button is clicked
 * @param {object}   cal      - Calendar adapter (default CAL_JALALI)
 */
export function renderPickerOverlay(el, curY, curM, onSelect, onClose, cal = CAL_JALALI) {
  el.innerHTML = ""
  el.dir = cal.dir
  let displayYear = curY

  const header = document.createElement("div")
  header.className = "flex items-center justify-between border-b border-neutral-100 px-3 py-2.5"

  const yearRow = document.createElement("div")
  yearRow.className = "flex items-center gap-1.5 text-sm font-medium text-neutral-800"

  const yearLabel = document.createElement("span")
  yearLabel.className = "min-w-14 text-center"
  yearLabel.textContent = cal.digits(displayYear)

  const prevYear = navButton(cal.prevIcon, () => { displayYear--; yearLabel.textContent = cal.digits(displayYear); buildGrid() })
  const nextYear = navButton(cal.nextIcon, () => { displayYear++; yearLabel.textContent = cal.digits(displayYear); buildGrid() })

  yearRow.append(prevYear, yearLabel, nextYear)

  const closeButton = navButton("ti-x", onClose)

  header.append(yearRow, closeButton)
  el.appendChild(header)

  const grid = document.createElement("div")
  grid.className = "grid grid-cols-4 gap-1.5 p-3"
  el.appendChild(grid)

  function buildGrid() {
    grid.innerHTML = ""
    const today = cal.today()
    cal.months.forEach((name, i) => {
      const m = i + 1
      const isCurrentMonth = displayYear === today.y && m === today.m
      const isActive = displayYear === curY && m === curM

      const cell = document.createElement("button")
      cell.type = "button"
      cell.className = monthCellClass({ isCurrentMonth, isActive })
      cell.textContent = name
      cell.onclick = () => onSelect(displayYear, m)
      grid.appendChild(cell)
    })
  }
  buildGrid()
}

function monthCellClass({ isCurrentMonth, isActive }) {
  const base = "rounded-lg border px-1 py-2 text-center text-xs transition-colors"

  if (isActive) return `${base} border-transparent bg-brand-600 font-medium text-white`
  if (isCurrentMonth) return `${base} border-brand-300 text-brand-600 hover:bg-neutral-50`
  return `${base} border-transparent text-neutral-600 hover:bg-neutral-100`
}

export function makeFooter(left, right) {
  const footer = document.createElement("div")
  footer.className = "flex items-center justify-between border-t border-neutral-100 px-3 py-2"
  if (left) footer.appendChild(left)
  if (right) footer.appendChild(right)
  return footer
}

export function makeTodayBtn(onClick) {
  const b = document.createElement("button")
  b.type = "button"
  b.className = "rounded-lg px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50"
  b.textContent = "امروز"
  b.onclick = onClick
  return b
}

export function makeHint(text) {
  const span = document.createElement("span")
  span.className = "text-xs text-neutral-400"
  span.textContent = text
  return span
}
